import classNames from "classnames";
import { useCallback, useState } from "react";
import { ChromePicker } from "react-color";
import { Tooltip } from "@zapier/design-system-beta";
import { Colors, Icon } from "@zapier/design-system";
import useUpdateNode from "../../hooks/useUpdateNode";
import { HexInput } from "../HexInput";
import styles from "./ColorPicker.module.css";

type Props = {
  id: string;
  color: string;
  showHexInput: boolean;
  colorOptions?: ColorOption[];
  onClose?: () => void;
};

type CustomButtonProps = {
  color: string;
  handleColorChange: (color: string) => void;
};

export type ColorOption = {
  label: string;
  color: string;
};

export const COLOR_PICKER_OPTIONS = [
  { label: "White", color: Colors.BackgroundDefault },
  { label: "Black", color: "#252525" },
  { label: "Yellow", color: "#F3D05E" },
  { label: "Orange", color: "#F78851" },
  { label: "Red", color: "#F35454" },
  { label: "Green", color: "#5CDD7E" },
  { label: "Blue", color: "#6BB7FA" },
  { label: "Peach", color: Colors.BrandPeach },
  { label: "Light Purple", color: Colors.BrandLavender },
  { label: "Dark Indigo", color: Colors.BrandNight },
  { label: "Light Aqua", color: Colors.BrandSky },
];

function CustomColorPicker({ color, handleColorChange }: CustomButtonProps) {
  return (
    <ChromePicker
      color={color}
      onChange={(color, event) => {
        event.stopPropagation();
        handleColorChange(color.hex);
      }}
      disableAlpha
    />
  );
}

export function ColorPicker({
  id,
  color,
  showHexInput,
  colorOptions = COLOR_PICKER_OPTIONS,
  onClose,
}: Props) {
  const updateNodeAndSave = useUpdateNode();
  const [isCustomPickerOpen, setIsCustomPickerOpen] = useState(false);
  const colorGroup1 = colorOptions.slice(0, 7); // Items 1–7
  const colorGroup2 = colorOptions.slice(7, 11); // Items 8–11

  const isPredefinedColor = colorOptions.some(
    (option) => option.color === color,
  );

  const handleColorChange = useCallback(
    (color: string) => {
      updateNodeAndSave({
        id,
        data: {
          color,
        },
      });
    },
    [updateNodeAndSave, id],
  );

  const openCustomColorPicker = useCallback(
    () => setIsCustomPickerOpen(true),
    [setIsCustomPickerOpen],
  );

  if (isCustomPickerOpen) {
    return (
      <CustomColorPicker color={color} handleColorChange={handleColorChange} />
    );
  }

  return (
    <div className={classNames(styles.colorPicker)}>
      <div className={styles.colorPickerGroupings}>
        <div className={styles.colorGroup}>
          {colorGroup1.map((option) => {
            const isChecked = color === option.color;

            return (
              <Tooltip
                key={option.color}
                placement="top"
                content={option.label}
              >
                <Tooltip.FocusableTrigger>
                  <label className={styles.colorOption}>
                    <input
                      type="radio"
                      name="color"
                      value={option.color}
                      checked={isChecked}
                      onChange={(event) =>
                        handleColorChange(event.target.value)
                      }
                      aria-label={`Select color ${option.color}`}
                    />
                    <div
                      className={classNames(styles.colorDotOuterBorder, {
                        [styles.colorDotOuterBorderChecked]: isChecked,
                      })}
                    >
                      <div
                        className={classNames(styles.colorDotInnerBorder, {
                          [styles.colorDotInnerBorderChecked]: isChecked,
                        })}
                      >
                        <span
                          className={classNames(styles.colorDot, {
                            [styles.colorDotChecked]: isChecked,
                          })}
                          style={{ backgroundColor: option.color }}
                        />
                      </div>
                    </div>
                  </label>
                </Tooltip.FocusableTrigger>
              </Tooltip>
            );
          })}
        </div>
        <div className={styles.colorGroup}>
          {colorGroup2.map((option) => {
            const isChecked = color === option.color;

            return (
              <Tooltip
                key={option.color}
                placement="top"
                content={option.label}
              >
                <Tooltip.FocusableTrigger>
                  <label className={styles.colorOption}>
                    <input
                      type="radio"
                      name="color"
                      value={option.color}
                      checked={isChecked}
                      onChange={(event) =>
                        handleColorChange(event.target.value)
                      }
                      aria-label={`Select color ${option.color}`}
                    />
                    <div
                      className={classNames(styles.colorDotOuterBorder, {
                        [styles.colorDotOuterBorderChecked]: isChecked,
                      })}
                    >
                      <div
                        className={classNames(styles.colorDotInnerBorder, {
                          [styles.colorDotInnerBorderChecked]: isChecked,
                        })}
                      >
                        <span
                          className={classNames(styles.colorDot, {
                            [styles.colorDotChecked]: isChecked,
                          })}
                          style={{ backgroundColor: option.color }}
                        />
                      </div>
                    </div>
                  </label>
                </Tooltip.FocusableTrigger>
              </Tooltip>
            );
          })}
        </div>
        <div className={styles.colorGroup}>
          <Tooltip placement="top" content="Custom">
            <Tooltip.FocusableTrigger>
              <label className={styles.colorOption}>
                <input
                  type="radio"
                  name="color"
                  value="custom"
                  onChange={openCustomColorPicker}
                  aria-label={`Select custom color`}
                />
                <div
                  className={classNames(styles.colorDotOuterBorder, {
                    [styles.colorDotOuterBorderChecked]: !isPredefinedColor,
                  })}
                >
                  <div
                    className={classNames(styles.colorDotInnerBorder, {
                      [styles.colorDotInnerBorderChecked]: !isPredefinedColor,
                    })}
                  >
                    <span
                      className={classNames(
                        styles.colorDot,
                        styles.customCircle,
                        {
                          [styles.colorDotChecked]: !isPredefinedColor,
                        },
                      )}
                    />
                  </div>
                </div>
              </label>
            </Tooltip.FocusableTrigger>
          </Tooltip>
          {showHexInput && (
            <div className={styles.hexInputWrapper}>
              <span className={styles.hashTag}>#</span>
              <HexInput id={id} color={color}>
                {(inputProps) => (
                  <input
                    type="text"
                    id="hexColor"
                    name="hexColor"
                    className={styles.hexInput}
                    {...inputProps}
                  />
                )}
              </HexInput>
            </div>
          )}
          {onClose && (
            <Tooltip placement="top" content="Close menu">
              <Tooltip.FocusableTrigger>
                <button
                  type="button"
                  className={styles.closeButton}
                  onClick={onClose}
                >
                  <Icon canAcceptPointerEvents={false} name="formX" size={20} />
                </button>
              </Tooltip.FocusableTrigger>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
}
